import React from 'react';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  MdCall,
  MdChecklist,
  MdExpandMore,
  MdDirections,
  MdEventNote,
  MdFitnessCenter,
} from 'react-icons/md';
import { AuthContext } from 'contexts/AuthContext';
import { app } from 'lib/firebase';
import {
  buildClassCompletionUpdate,
  buildLateCanceledClassEntries,
  buildDropClassUpdate,
  getClassDisplayText,
  formatClassDateTimeLA,
  getClassDurationMinutes,
  getDropClassCandidates,
  getInstructorClassActionState,
  getNeedSubWithinTwoHoursMessage,
} from 'utils/instructorClassWorkflows';
import {
  buildClassActivityActor,
  buildInstructorClassCompletionActivity,
  buildInstructorClassDropActivity,
} from 'utils/classActivityLogger';
import { getClassTypeIcon } from 'utils/classIcons';
import {
  ACCOUNT_STATUS_STRIKE_PAUSED,
  buildLateCancellationStrikeStateAfterDrop,
  getLateCancellationStrikeImpact,
} from 'utils/lateCancellationStrikes';
import { buildStrikePausedTeamEmailPayload } from 'utils/lateCancellationStrikeNotifications';

const db = getFirestore(app);
const DROP_REASONS = [
  { value: 'Sick', label: 'sick' },
  { value: 'Weather', label: 'weather' },
  { value: 'Family', label: 'family' },
  { value: 'Emergency', label: 'emergency' },
  { value: 'Other', label: 'other' },
];
const PEOPLE_OPTIONS = ['0', '1-2', '2-5', '5-10', '10-15', '15+'];

const firstDisplayValue = (...values) => {
  const found = values.find((value) => {
    if (value === null || value === undefined) return false;
    return String(getClassDisplayText(value, '')).trim() !== '';
  });
  return found;
};

function DetailItem({ label, value }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');

  return (
    <Box>
      <Text color={mutedText} fontSize="xs" fontWeight="700" textTransform="uppercase">
        {label}
      </Text>
      <Text color={textColor} fontSize="sm" fontWeight="700" mt="4px">
        {getClassDisplayText(value)}
      </Text>
    </Box>
  );
}

function getFacilityCoordinates(facility, classData) {
  const location = facility?.location || classData?.location;
  const latitude = location?.latitude || facility?.latitude || classData?.latitude;
  const longitude = location?.longitude || facility?.longitude || classData?.longitude;

  if (!latitude || !longitude) return null;

  return {
    latitude,
    longitude,
  };
}

const hasInstructionContent = (value) => (
  value !== null
  && value !== undefined
  && String(value).trim() !== ''
);

const findInstructionField = (note, fieldNames) => {
  if (!note || typeof note !== 'object') return undefined;
  const entryByNormalizedKey = Object.entries(note).reduce((acc, [key, value]) => {
    acc[key.toLowerCase()] = value;
    return acc;
  }, {});
  const matchingFieldName = fieldNames.find((fieldName) => (
    Object.prototype.hasOwnProperty.call(entryByNormalizedKey, fieldName.toLowerCase())
  ));
  return matchingFieldName ? entryByNormalizedKey[matchingFieldName.toLowerCase()] : undefined;
};

const hasAnyInstructionField = (note, fieldNames) => (
  findInstructionField(note, fieldNames) !== undefined
);

const INSTRUCTION_TITLE_FIELDS = [
  'title',
  'name',
  'label',
  'heading',
  'question',
  'prompt',
  'key',
  'type',
];

const INSTRUCTION_VALUE_FIELDS = [
  'description',
  'value',
  'body',
  'text',
  'answer',
  'details',
  'detail',
  'note',
  'notes',
  'instruction',
  'instructions',
  'response',
  'selected',
];

const INSTRUCTION_IMAGE_FIELDS = ['image', 'imageUrl', 'url', 'icon'];

const isInstructionImageValue = (value) => (
  typeof value === 'string'
  && /^(https?:\/\/|data:image\/|blob:)/i.test(value.trim())
);

const getFallbackObjectValue = (note) => Object.entries(note)
  .filter(([, value]) => hasInstructionContent(value))
  .map(([key, value]) => `${key}: ${getClassDisplayText(value)}`)
  .join('\n');

const normalizeInstructionNote = (note, fallbackTitle) => {
  if (['string', 'number', 'boolean'].includes(typeof note)) {
    return {
      title: 'Instructions',
      value: String(note),
      image: '',
    };
  }

  if (!note || typeof note !== 'object') {
    return {
      title: fallbackTitle,
      value: '',
      image: '',
    };
  }

  const title = getClassDisplayText(
    findInstructionField(note, INSTRUCTION_TITLE_FIELDS),
    fallbackTitle || 'Instructions'
  );
  const rawValue = findInstructionField(note, INSTRUCTION_VALUE_FIELDS);
  const fallbackValue = rawValue === undefined ? getFallbackObjectValue(note) : '';
  const value = getClassDisplayText(rawValue ?? fallbackValue, '');
  const rawImage = findInstructionField(note, INSTRUCTION_IMAGE_FIELDS);

  return {
    title,
    value: value === title ? '' : value,
    image: isInstructionImageValue(rawImage) ? rawImage : '',
  };
};

const normalizeInstructionNotes = (value, fallbackTitle) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((note) => normalizeInstructionNote(note, fallbackTitle))
      .filter((note) => hasInstructionContent(note.title) || hasInstructionContent(note.value));
  }

  if (typeof value === 'object') {
    const isSingleNote = hasAnyInstructionField(value, [
      ...INSTRUCTION_TITLE_FIELDS,
      ...INSTRUCTION_VALUE_FIELDS,
      ...INSTRUCTION_IMAGE_FIELDS,
    ]);
    const notes = isSingleNote
      ? [value]
      : Object.entries(value).map(([key, note]) => (
        note && typeof note === 'object'
          ? normalizeInstructionNote(note, key)
          : { title: key, value: getClassDisplayText(note, ''), image: '' }
      ));

    return notes
      .map((note) => (
        note?.title && Object.prototype.hasOwnProperty.call(note, 'value')
          ? note
          : normalizeInstructionNote(note, fallbackTitle)
      ))
      .filter((note) => hasInstructionContent(note.title) || hasInstructionContent(note.value));
  }

  return [normalizeInstructionNote(value, fallbackTitle)];
};

const getFirstInstructionValue = (fallbackTitle, ...values) => (
  values.find((value) => normalizeInstructionNotes(value, fallbackTitle).length > 0) || null
);

function InstructionPanel({ icon, title, value, colorScheme = 'blue' }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const bg = useColorModeValue(`${colorScheme}.50`, 'whiteAlpha.100');
  const rowBg = useColorModeValue('whiteAlpha.800', 'whiteAlpha.100');
  const iconColor = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.300`);
  const borderColor = useColorModeValue(`${colorScheme}.100`, 'whiteAlpha.200');
  const notes = normalizeInstructionNotes(value, title);

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      bg={bg}
      borderRadius="14px"
      p="16px"
    >
      <Flex gap="12px" align="center" mb="12px">
        <Flex
          w="38px"
          h="38px"
          borderRadius="12px"
          bg="whiteAlpha.700"
          align="center"
          justify="center"
          flexShrink="0"
        >
          <Icon as={icon} color={iconColor} w="22px" h="22px" />
        </Flex>
        <Box minW="0">
          <Text color={textColor} fontSize="sm" fontWeight="800">
            {title}
          </Text>
          <Text color={mutedText} fontSize="xs" mt="2px">
            {notes.length > 0 ? `${notes.length} instruction${notes.length === 1 ? '' : 's'}` : 'No instructions set'}
          </Text>
        </Box>
      </Flex>

      <Flex direction="column" gap="8px">
        {notes.length > 0 ? notes.map((note, index) => (
          <Flex
            key={`${note.title}-${index}`}
            align="flex-start"
            gap="10px"
            bg={rowBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="12px"
            p="10px"
          >
            {note.image ? (
              <Image
                src={note.image}
                alt={note.title}
                boxSize="24px"
                objectFit="contain"
                flexShrink={0}
              />
            ) : (
              <Flex
                w="24px"
                h="24px"
                borderRadius="8px"
                bg={bg}
                align="center"
                justify="center"
                flexShrink={0}
              >
                <Icon as={icon} color={iconColor} w="15px" h="15px" />
              </Flex>
            )}
            <Box minW="0" flex="1">
              <Text color={textColor} fontSize="sm" fontWeight="800" lineHeight="1.25">
                {note.title}
              </Text>
              {note.value ? (
                <Text color={mutedText} fontSize="sm" mt="4px" whiteSpace="pre-wrap" lineHeight="1.45">
                  {note.value}
                </Text>
              ) : null}
            </Box>
          </Flex>
        )) : (
          <Text color={mutedText} fontSize="sm">
            No instructions set.
          </Text>
        )}
      </Flex>
    </Box>
  );
}

export default function InstructorClassDetailsModal({
  isOpen,
  onClose,
  classData,
  facility,
  onClassUpdated,
  onClaimClass,
  isClaiming = false,
  canClaim = true,
}) {
  const { user, userDocId, userData } = React.useContext(AuthContext);
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const modalBodyBg = useColorModeValue('gray.50', 'navy.900');
  const sectionBg = useColorModeValue('white', 'navy.800');
  const mapBorderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const cardBorderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const headerBg = useColorModeValue('linear-gradient(135deg, #2B84C4 0%, #38BDF8 100%)', 'linear-gradient(135deg, #1A365D 0%, #2B84C4 100%)');

  const [drove, setDrove] = React.useState('');
  const [people, setPeople] = React.useState('');
  const [expected, setExpected] = React.useState('');
  const [feedback, setFeedback] = React.useState('');
  const [concern, setConcern] = React.useState('');
  const [dropReason, setDropReason] = React.useState('');
  const [dropReasonOther, setDropReasonOther] = React.useState('');
  const [dropClassCandidates, setDropClassCandidates] = React.useState([]);
  const [selectedDropClassIds, setSelectedDropClassIds] = React.useState([]);
  const [isLoadingDropSeries, setIsLoadingDropSeries] = React.useState(false);
  const [isDropConfirmOpen, setIsDropConfirmOpen] = React.useState(false);
  const [isSavingCompletion, setIsSavingCompletion] = React.useState(false);
  const [isDropping, setIsDropping] = React.useState(false);
  const [facilityUserDetails, setFacilityUserDetails] = React.useState(null);
  const dropConfirmCancelRef = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setDrove('');
    setPeople('');
    setExpected('');
    setFeedback('');
    setConcern('');
    setDropReason('');
    setDropReasonOther('');
    setDropClassCandidates([]);
    setSelectedDropClassIds(classData?.id ? [classData.id] : []);
    setIsDropConfirmOpen(false);
  }, [isOpen, classData?.id]);

  React.useEffect(() => {
    setFacilityUserDetails(null);

    if (!isOpen || !classData?.FacilityID) return undefined;

    let isActive = true;

    getDoc(doc(db, 'Users', classData.FacilityID))
      .then((facilitySnapshot) => {
        if (!isActive) return;
        setFacilityUserDetails(
          facilitySnapshot?.exists?.() ? facilitySnapshot.data() : null
        );
      })
      .catch((error) => {
        console.error('Error loading facility contact details:', error);
        if (isActive) setFacilityUserDetails(null);
      });

    return () => {
      isActive = false;
    };
  }, [isOpen, classData?.FacilityID]);

  const classRecord = classData || {};
  const facilityDetails = {
    ...(facility || {}),
    ...(facilityUserDetails || {}),
  };
  const assignedInstructorId = String(classRecord.AssignedInstructorID || '').trim();
  const isAssignedToCurrentInstructor = Boolean(
    userDocId && assignedInstructorId && assignedInstructorId === userDocId
  );
  const actionState = getInstructorClassActionState({
    classData: classRecord,
    isAssignedToCurrentInstructor,
  });
  const durationMinutes = getClassDurationMinutes(classRecord);
  const classTitle = getClassDisplayText(
    classRecord.ClassName || classRecord.ClassType,
    'Class Details'
  );
  const classIcon = getClassTypeIcon(classRecord.ClassName || classRecord.ClassType);
  const facilityName = getClassDisplayText(
    firstDisplayValue(
      facilityUserDetails?.FacilityName,
      facilityUserDetails?.FirstName,
      facility?.FacilityName,
      facility?.FirstName,
      classRecord.FacilityName
    ),
    ''
  );
  const facilityAddress = getClassDisplayText(
    firstDisplayValue(facilityUserDetails?.Address, facility?.Address, classRecord.Address),
    ''
  );
  const facilityContact = getClassDisplayText(
    firstDisplayValue(
      facilityUserDetails?.ContactName,
      facilityUserDetails?.contactName,
      facilityUserDetails?.FirstName,
      facility?.ContactName,
      facility?.FirstName,
      classRecord.FacilityContact
    ),
    ''
  );
  const facilityPhone = getClassDisplayText(
    firstDisplayValue(facilityUserDetails?.Phone, facility?.Phone, classRecord.FacilityPhone),
    ''
  );
  const facilityEmail = getClassDisplayText(
    firstDisplayValue(
      facilityUserDetails?.Email,
      facilityUserDetails?.InvoicingEmail,
      facility?.Email,
      facility?.InvoicingEmail,
      classRecord.FacilityEmail
    ),
    ''
  );
  const classTypeDisplay = getClassDisplayText(classRecord.ClassType || classRecord.ClassName, '');
  const facilityCoordinates = getFacilityCoordinates(facilityDetails, classRecord);
  const withinTwoHoursMessage = getNeedSubWithinTwoHoursMessage({
    facilityName: facilityContact || facilityName,
    facilityPhone,
  });
  const mapSearch = facilityCoordinates
    ? `${facilityCoordinates.latitude},${facilityCoordinates.longitude}`
    : facilityAddress;
  const mapUrl = mapSearch
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapSearch)}`
    : '';
  const mapEmbedUrl = facilityCoordinates
    ? `https://www.google.com/maps?q=${facilityCoordinates.latitude},${facilityCoordinates.longitude}&output=embed`
    : '';
  const preClassInstructions = getFirstInstructionValue(
    'Instructor Instructions',
    classRecord.PreClassNotes,
    classRecord.preClassNotes,
    classRecord.PreClassNote,
    classRecord.preClassNote,
    classRecord.InstructorInstructions,
    classRecord.instructorInstructions
  );
  const dayOfInstructions = getFirstInstructionValue(
    'Day-Of Instructions',
    classRecord.dayOfNotes,
    classRecord.DayOfNotes,
    classRecord.dayOfNote,
    classRecord.DayOfNote,
    classRecord.DayOfInstructions,
    classRecord.dayOfInstructions
  );
  const classActivityActor = buildClassActivityActor({ user, userDocId, userData });
  const assignmentBadge = actionState.isOpportunity
    ? 'Available Opportunity'
    : isAssignedToCurrentInstructor
      ? 'Assigned to You'
      : assignedInstructorId
        ? 'Assigned'
        : 'Not Assigned';
  const isCompletionFormValid = drove === 'no' || (
    drove === 'yes'
    && people
    && expected
    && (expected !== 'no' || concern.trim())
  );
  const selectedDropClassesForStrike = dropClassCandidates.filter((dropClass) => (
    selectedDropClassIds.includes(dropClass.id)
  ));
  const strikeImpact = getLateCancellationStrikeImpact({
    accountStatus: userData?.AccountStatus,
    lateCanceledClassArray: userData?.lateCanceledClassArray || [],
    selectedDropClasses: selectedDropClassesForStrike,
    strikeState: userData?.lateCancellationStrikeState || {},
  });
  const strikeWarningTitle = strikeImpact.newStrikeCount > 0
    ? strikeImpact.shouldStrikePause ? 'Are you sure?' : 'Late Cancellation Warning'
    : '';
  const strikeWarningCopy = strikeImpact.newStrikeCount > 0
    ? strikeImpact.shouldStrikePause
      ? 'You are about to late cancel for the third time. This may deactivate your profile.'
      : strikeImpact.projectedStrikeCount === 2
        ? 'This late cancellation will be recorded on your profile. One more late cancellation may pause your account and drop your future assigned classes.'
        : '3 cancellations within 6 months could pause your Fit Group USA profile and prevent you from picking up future opportunities.'
    : '';

  React.useEffect(() => {
    if (!isOpen || !classData?.id || !userDocId) return undefined;

    let isActive = true;

    const loadDropClasses = async () => {
      if (!actionState.canRequestSub) {
        if (isActive) {
          setDropClassCandidates([]);
          setSelectedDropClassIds([]);
        }
        return;
      }

      setIsLoadingDropSeries(Boolean(classData.RepeatClassIdentifier));
      try {
        let seriesClasses = [classData];

        if (classData.RepeatClassIdentifier) {
          const seriesQuery = query(
            collection(db, 'Classes'),
            where('RepeatClassIdentifier', '==', classData.RepeatClassIdentifier),
            where('AssignedInstructorID', '==', userDocId)
          );
          const seriesSnapshot = await getDocs(seriesQuery);
          seriesClasses = seriesSnapshot.docs.map((classDoc) => ({
            id: classDoc.id,
            ...classDoc.data(),
          }));
        }

        const candidates = getDropClassCandidates({
          currentClass: classData,
          seriesClasses,
          instructorId: userDocId,
        });

        if (!isActive) return;
        setDropClassCandidates(candidates.length > 0 ? candidates : [classData]);
        setSelectedDropClassIds([classData.id]);
      } catch (error) {
        console.error('Error loading repeat classes for sub request:', error);
        if (isActive) {
          setDropClassCandidates([classData]);
          setSelectedDropClassIds([classData.id]);
        }
      } finally {
        if (isActive) setIsLoadingDropSeries(false);
      }
    };

    loadDropClasses();

    return () => {
      isActive = false;
    };
  }, [
    isOpen,
    classData,
    classData?.id,
    classData?.RepeatClassIdentifier,
    actionState.canRequestSub,
    userDocId,
  ]);

  if (!classData) return null;

  const handleCompleteClass = async () => {
    if (!isCompletionFormValid) {
      toast({
        title: 'Complete the required fields',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setIsSavingCompletion(true);
    try {
      const update = buildClassCompletionUpdate({
        drove: drove === 'yes',
        people,
        expected,
        feedback,
        concern,
        completedBy: userDocId,
      });

      const batch = writeBatch(db);
      const classRef = doc(db, 'Classes', classData.id);
      const activityRef = doc(collection(db, 'Classes', classData.id, 'Activity'));
      batch.update(classRef, update);
      batch.set(activityRef, buildInstructorClassCompletionActivity({
        actor: classActivityActor,
        classId: classData.id,
        classData,
        update,
        drove: drove === 'yes',
        people,
        expected,
        feedback,
        concern,
      }));
      await batch.commit();
      toast({
        title: 'Class completion saved',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      onClassUpdated?.();
      onClose();
    } catch (error) {
      toast({
        title: 'Unable to save completion',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSavingCompletion(false);
    }
  };

  const handleToggleDropClass = (classId) => {
    setSelectedDropClassIds((current) => (
      current.includes(classId)
        ? current.filter((id) => id !== classId)
        : [...current, classId]
    ));
  };

  const openDropConfirmation = () => {
    if (!actionState.canRequestSub) return;
    if (!dropReason) {
      toast({
        title: 'Select a cancellation reason',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    if (selectedDropClassIds.length === 0) {
      toast({
        title: 'Select at least one class',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setIsDropConfirmOpen(true);
  };

  const handleNeedSub = async () => {
    if (!actionState.canRequestSub || !dropReason || selectedDropClassIds.length === 0) return;

    const selectedDropClasses = dropClassCandidates.filter((dropClass) => (
      selectedDropClassIds.includes(dropClass.id)
    ));
    const cancellationTime = Date.now();
    const currentStrikeImpact = getLateCancellationStrikeImpact({
      accountStatus: userData?.AccountStatus,
      lateCanceledClassArray: userData?.lateCanceledClassArray || [],
      selectedDropClasses,
      nowMs: cancellationTime,
      cancellationTime,
      strikeState: userData?.lateCancellationStrikeState || {},
    });
    setIsDropping(true);
    try {
      const lateCanceledClassEntries = buildLateCanceledClassEntries({
        selectedDropClasses,
        reason: dropReason,
        reasonOther: dropReasonOther,
        cancellationTime,
        nowMs: cancellationTime,
        existingStrikeCount: currentStrikeImpact.currentStrikeCount,
      });
      const lateCanceledEntryByClassId = lateCanceledClassEntries.reduce((entriesById, entry) => ({
        ...entriesById,
        [entry.ClassID]: entry,
      }), {});
      const batch = writeBatch(db);
      selectedDropClasses.forEach((dropClass) => {
        const cancellationEntry = lateCanceledEntryByClassId[dropClass.id];
        const update = buildDropClassUpdate({
          reason: dropReason,
          instructorId: userDocId,
          isWithinTwoHours: cancellationEntry?.WithinTwoHours === true,
          isWithinTwentyFourHours: cancellationEntry?.WithinTwentyFourHours === true,
          isStrikeEligible: cancellationEntry?.StrikeEligible === true,
        });
        const classRef = doc(db, 'Classes', dropClass.id);
        const activityRef = doc(collection(db, 'Classes', dropClass.id, 'Activity'));
        batch.update(classRef, update);
        batch.set(activityRef, buildInstructorClassDropActivity({
          actor: classActivityActor,
          instructorId: userDocId,
          classId: dropClass.id,
          classData: dropClass,
          reason: dropReason === 'Other' ? dropReasonOther || dropReason : dropReason,
          isWithinTwoHours: cancellationEntry?.WithinTwoHours === true,
          isWithinTwentyFourHours: cancellationEntry?.WithinTwentyFourHours === true,
          isStrikeEligible: cancellationEntry?.StrikeEligible === true,
        }));
      });

      const droppedFutureClassIds = [];
      const droppedFutureClasses = [];
      if (currentStrikeImpact.shouldStrikePause) {
        const futureClassesSnapshot = await getDocs(query(
          collection(db, 'Classes'),
          where('AssignedInstructorID', '==', userDocId)
        ));
        const selectedDropClassIdSet = new Set(selectedDropClasses.map((dropClass) => dropClass.id));
        futureClassesSnapshot.docs.forEach((classSnapshot) => {
          const futureClassData = { id: classSnapshot.id, ...classSnapshot.data() };
          const futureClassDate = Number(futureClassData.Date || 0);
          if (
            selectedDropClassIdSet.has(classSnapshot.id)
            || !Number.isFinite(futureClassDate)
            || futureClassDate < cancellationTime
          ) {
            return;
          }

          droppedFutureClassIds.push(classSnapshot.id);
          droppedFutureClasses.push({
            id: classSnapshot.id,
            className: futureClassData.ClassName,
            date: futureClassData.Date,
            facilityId: futureClassData.FacilityID,
          });
          const classRef = doc(db, 'Classes', classSnapshot.id);
          const activityRef = doc(collection(db, 'Classes', classSnapshot.id, 'Activity'));
          batch.update(classRef, {
            AssignedInstructorID: '',
            classStatus: 3,
          });
          batch.set(activityRef, buildInstructorClassDropActivity({
            actor: classActivityActor,
            instructorId: userDocId,
            classId: classSnapshot.id,
            classData: futureClassData,
            reason: 'Third strike late cancellation',
            isWithinTwoHours: false,
            isWithinTwentyFourHours: false,
            isStrikeEligible: false,
          }));
        });
      }

      const userUpdate = {
        lateCanceledClassArray: arrayUnion(...lateCanceledClassEntries),
        lateCancellationStrikeState: buildLateCancellationStrikeStateAfterDrop({
          impact: currentStrikeImpact,
          droppedFutureClassIds,
          nowMs: cancellationTime,
          actor: classActivityActor,
        }),
        ...(currentStrikeImpact.shouldStrikePause ? {
          AccountStatus: ACCOUNT_STATUS_STRIKE_PAUSED,
        } : {}),
      };
      batch.update(doc(db, 'Users', userDocId), userUpdate);
      if (currentStrikeImpact.shouldStrikePause) {
        batch.set(doc(collection(db, 'Users', userDocId, 'Activity')), {
          title: 'Instructor Strike Paused',
          body: 'This was your third late cancellation. Your profile has been paused and all future assigned classes have been dropped. You can submit one appeal from your profile.',
          type: 'late_cancellation_strike_paused',
          timestamp: cancellationTime,
          user: classActivityActor?.email || userData?.Email || user?.email || '',
          metadata: {
            strikeEligibleClassIds: currentStrikeImpact.strikeEligibleClassIds,
            droppedFutureClassIds,
            source: 'instructor_portal',
          },
        });
        batch.set(doc(collection(db, 'mail')), buildStrikePausedTeamEmailPayload({
          instructor: {
            id: userDocId,
            ...userData,
          },
          actor: classActivityActor,
          reason: dropReason === 'Other' ? dropReasonOther || dropReason : dropReason,
          source: 'instructor_portal',
          nowMs: cancellationTime,
          strikeEligibleClassIds: currentStrikeImpact.strikeEligibleClassIds,
          droppedClasses: droppedFutureClasses,
        }));
      }
      await batch.commit();
      toast({
        title: currentStrikeImpact.shouldStrikePause
          ? 'Profile Paused'
          : selectedDropClasses.length === 1 ? 'Class opened for a sub' : 'Classes opened for subs',
        description: currentStrikeImpact.shouldStrikePause
          ? 'This was your third late cancellation. Your profile has been paused and all future assigned classes have been dropped. You can submit one appeal from your profile.'
          : undefined,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      setIsDropConfirmOpen(false);
      onClassUpdated?.();
      onClose();
    } catch (error) {
      console.error('Unable to request a sub:', error);
      toast({
        title: 'Unable to request a sub',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsDropping(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent borderRadius="20px" overflow="hidden" maxH="92vh">
        <ModalHeader bg={headerBg} color="white" borderBottomWidth="1px" borderColor="whiteAlpha.300">
          <Flex direction="column" gap="8px">
            <Flex align="center" gap="10px" wrap="wrap">
              <Flex
                w="58px"
                h="58px"
                borderRadius="18px"
                bg="white"
                align="center"
                justify="center"
                flexShrink="0"
                boxShadow="0 10px 25px rgba(0, 0, 0, 0.14)"
              >
                {classIcon ? (
                  <Image
                    src={classIcon}
                    alt={`${classTitle} class icon`}
                    w="40px"
                    h="40px"
                    objectFit="contain"
                  />
                ) : (
                  <Icon as={MdFitnessCenter} color="brand.500" w="28px" h="28px" />
                )}
              </Flex>
              <Box minW="0">
                <Text color="white" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" lineHeight="1.2">
                  {classTitle}
                </Text>
                <Text color="whiteAlpha.900" fontSize="sm" fontWeight="600" mt="4px">
                  {formatClassDateTimeLA(classData.Date)}
                </Text>
              </Box>
              <Badge bg="whiteAlpha.300" color="white" borderRadius="8px" px="10px" py="4px">
                {durationMinutes} min
              </Badge>
              <Badge bg={actionState.isOpportunity ? 'orange.400' : 'green.400'} color="white" borderRadius="8px" px="10px" py="4px">
                {assignmentBadge}
              </Badge>
            </Flex>
          </Flex>
        </ModalHeader>
        <ModalCloseButton color="white" />

        <ModalBody bg={modalBodyBg} p={{ base: '18px', md: '24px' }}>
          <SimpleGrid columns={{ base: 1, lg: 12 }} gap="20px" alignItems="start">
            <Flex gridColumn={{ base: 'span 1', lg: 'span 7' }} direction="column" gap="16px">
              <Box bg={sectionBg} p="20px" borderRadius="16px" boxShadow="sm" border="1px solid" borderColor={cardBorderColor}>
              <Text color={textColor} fontSize="lg" fontWeight="800" mb="14px">
                Facility
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
                <DetailItem label="Facility" value={facilityName} />
                <DetailItem label="Address" value={facilityAddress} />
                <DetailItem label="Facility Contact" value={facilityContact} />
                <DetailItem label="Facility Phone" value={facilityPhone} />
                <DetailItem label="Facility Email" value={facilityEmail} />
                <DetailItem label="Class Type" value={classTypeDisplay} />
                <DetailItem label="Description" value={classData.ClasssDescription} />
              </SimpleGrid>
              {mapEmbedUrl && (
                <Box
                  mt="18px"
                  borderRadius="16px"
                  overflow="hidden"
                  border="1px solid"
                  borderColor={mapBorderColor}
                  h={{ base: '210px', md: '260px' }}
                >
                  <iframe
                    title="Facility location map"
                    src={mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                  />
                </Box>
              )}
              {mapUrl && (
                <Button
                  as="a"
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  mt="14px"
                  size="sm"
                  leftIcon={<Icon as={MdDirections} />}
                  colorScheme="brand"
                  variant="outline"
                >
                  Open Directions
                </Button>
              )}
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
                <InstructionPanel
                  icon={MdChecklist}
                  title="Instructor Instructions"
                  value={preClassInstructions}
                  colorScheme="blue"
                />
                <InstructionPanel
                  icon={MdEventNote}
                  title="Day-Of Instructions"
                  value={dayOfInstructions}
                  colorScheme="teal"
                />
              </SimpleGrid>
            </Flex>

            <Flex gridColumn={{ base: 'span 1', lg: 'span 5' }} direction="column" gap="16px">
              {!actionState.canGetPaid && (
                <Box bg={sectionBg} p="20px" borderRadius="16px" boxShadow="sm" border="1px solid" borderColor={cardBorderColor}>
                  <Text color={textColor} fontSize="lg" fontWeight="800" mb="14px">
                    {actionState.canRequestSub
                      ? 'Need a Sub?'
                      : actionState.isOpportunity
                        ? 'Opportunity'
                        : 'Status'}
                  </Text>
                  {actionState.isCompleted ? (
                  <Alert status="success" borderRadius="12px">
                    <AlertIcon />
                    <AlertDescription>
                      This class is already completed.
                    </AlertDescription>
                  </Alert>
                ) : actionState.isOpportunity ? (
                  <Flex direction="column" gap="12px">
                    <Badge alignSelf="flex-start" colorScheme="orange" borderRadius="8px" px="10px" py="4px">
                      Available Opportunity
                    </Badge>
                    <Text color={mutedText} fontSize="sm">
                      This class is open and not assigned to you yet. Claim it from the opportunities flow before class actions become available.
                    </Text>
                    {onClaimClass && (
                      <Button
                        colorScheme="brand"
                        onClick={onClaimClass}
                        isLoading={isClaiming}
                        isDisabled={!canClaim}
                      >
                        Claim Class
                      </Button>
                    )}
                  </Flex>
                ) : actionState.reason === 'assigned-to-other-instructor' ? (
                  <Alert status="info" borderRadius="12px">
                    <AlertIcon />
                    <AlertDescription>
                      This class is assigned to another instructor, so instructor actions are not available here.
                    </AlertDescription>
                  </Alert>
                ) : actionState.canRequestSub ? (
                  <Flex direction="column" gap="12px">
                    {actionState.requiresFacilityCallBeforeDrop && (
                      <Alert status="warning" borderRadius="12px">
                        <AlertIcon />
                        <AlertDescription>
                          {withinTwoHoursMessage}
                        </AlertDescription>
                      </Alert>
                    )}
                    {actionState.requiresFacilityCallBeforeDrop && facilityPhone && (
                      <Button
                        as="a"
                        href={`tel:${facilityPhone}`}
                        colorScheme="orange"
                        variant="outline"
                        alignSelf="flex-start"
                        leftIcon={<Icon as={MdCall} />}
                      >
                        Call Facility
                      </Button>
                    )}
                    {strikeWarningCopy && (
                      <Alert
                        status={strikeImpact.shouldStrikePause ? 'error' : 'warning'}
                        borderRadius="12px"
                      >
                        <AlertIcon />
                        <Box>
                          <AlertTitle>{strikeWarningTitle}</AlertTitle>
                          <AlertDescription>
                            {strikeWarningCopy}
                          </AlertDescription>
                        </Box>
                      </Alert>
                    )}
                    <Box>
                      <Text color={textColor} fontSize="sm" fontWeight="800" mb="8px">
                        1. Please indicate why you need a sub for the selected class
                      </Text>
                      <FormControl>
                        <FormLabel>Cancellation reason</FormLabel>
                        <Select
                          aria-label="Cancellation reason"
                          value={dropReason}
                          onChange={(event) => setDropReason(event.target.value)}
                          placeholder="Select a reason"
                        >
                          {DROP_REASONS.map((reason) => (
                            <option key={reason.value} value={reason.value}>
                              {reason.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                      {dropReason === 'Other' && (
                        <FormControl mt="10px">
                          <FormLabel>Tell us what happened</FormLabel>
                          <Textarea
                            aria-label="Tell us what happened"
                            value={dropReasonOther}
                            onChange={(event) => setDropReasonOther(event.target.value)}
                            placeholder="Add a short note"
                          />
                        </FormControl>
                      )}
                    </Box>
                    <Box>
                      <Text color={textColor} fontSize="sm" fontWeight="800" mb="8px">
                        2. Please select the class (or classes) for which you would like to receive a sub
                      </Text>
                      {isLoadingDropSeries ? (
                        <Text color={mutedText} fontSize="sm">
                          Loading upcoming classes in this series...
                        </Text>
                      ) : (
                        <Flex direction="column" gap="8px">
                          {dropClassCandidates.map((dropClass) => (
                            <Checkbox
                              key={dropClass.id}
                              isChecked={selectedDropClassIds.includes(dropClass.id)}
                              onChange={() => handleToggleDropClass(dropClass.id)}
                            >
                              <Text as="span" fontSize="sm" fontWeight="700">
                                {getClassDisplayText(dropClass.ClassName || dropClass.ClassType, 'Class')}
                              </Text>
                              <Text as="span" color={mutedText} fontSize="xs" ml="6px">
                                {formatClassDateTimeLA(dropClass.Date)}
                              </Text>
                            </Checkbox>
                          ))}
                        </Flex>
                      )}
                    </Box>
                    <Box>
                      <Text color={textColor} fontSize="sm" fontWeight="800" mb="8px">
                        3. Tap "Get a sub" button below
                      </Text>
                      <Text color={mutedText} fontSize="sm">
                        Requesting a sub will remove you from the selected class{selectedDropClassIds.length === 1 ? '' : 'es'}.
                      </Text>
                    </Box>
                    <Button
                      variant="outline"
                      colorScheme="orange"
                      onClick={openDropConfirmation}
                      isLoading={isDropping}
                      isDisabled={!dropReason || selectedDropClassIds.length === 0}
                    >
                      Get a sub
                    </Button>
                  </Flex>
                ) : (
                  <Alert status="info" borderRadius="12px">
                    <AlertIcon />
                    <AlertDescription>
                      No action is needed for this class right now.
                    </AlertDescription>
                  </Alert>
                )}
                </Box>
              )}

              {actionState.canGetPaid && (
                <Box id="completion-form" bg={sectionBg} p="20px" borderRadius="16px" boxShadow="sm">
                  <Alert status="warning" borderRadius="12px" mb="14px">
                    <AlertIcon />
                    <AlertDescription>
                      You will not be paid unless you complete this step.
                    </AlertDescription>
                  </Alert>
                  <Text color={textColor} fontSize="lg" fontWeight="800" mb="14px">
                    Class Completion Form
                  </Text>
                  <FormControl mb="14px">
                    <FormLabel>Did you teach this class?</FormLabel>
                    <RadioGroup aria-label="Did you teach this class?" value={drove} onChange={setDrove}>
                      <Flex gap="18px">
                        <Radio value="yes">Yes</Radio>
                        <Radio value="no">No</Radio>
                      </Flex>
                    </RadioGroup>
                  </FormControl>
                  {drove === 'yes' && (
                    <Flex direction="column" gap="12px">
                      <FormControl>
                        <FormLabel>People</FormLabel>
                        <Menu matchWidth>
                          <MenuButton
                            as={Button}
                            aria-label="How many people attended?"
                            variant="outline"
                            rightIcon={<Icon as={MdExpandMore} />}
                            justifyContent="space-between"
                            w="100%"
                            textAlign="left"
                            fontWeight="600"
                          >
                            {people || 'Select attendance'}
                          </MenuButton>
                          <MenuList zIndex="popover">
                            {PEOPLE_OPTIONS.map((option) => (
                              <MenuItem key={option} onClick={() => setPeople(option)}>
                                {option}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Menu>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Did class go as expected?</FormLabel>
                        <RadioGroup aria-label="Did class go as expected?" value={expected} onChange={setExpected}>
                          <Flex gap="18px">
                            <Radio value="yes">Yes, class went as expected</Radio>
                            <Radio value="no">No, something was off</Radio>
                          </Flex>
                        </RadioGroup>
                      </FormControl>
                      {expected === 'no' && (
                        <FormControl>
                          <FormLabel>Concern / Details</FormLabel>
                          <Textarea
                            aria-label="Concern / Details"
                            value={concern}
                            onChange={(event) => setConcern(event.target.value)}
                          />
                        </FormControl>
                      )}
                      <FormControl>
                        <FormLabel>Additional details / feedback</FormLabel>
                        <Textarea
                          value={feedback}
                          onChange={(event) => setFeedback(event.target.value)}
                        />
                      </FormControl>
                    </Flex>
                  )}
                  {drove === 'no' && (
                    <Alert status="warning" borderRadius="12px">
                      <AlertIcon />
                      <AlertDescription>
                        Confirm you did not teach this class
                      </AlertDescription>
                    </Alert>
                  )}
                  <Button
                    mt="16px"
                    w="100%"
                    colorScheme="brand"
                    onClick={handleCompleteClass}
                    isLoading={isSavingCompletion}
                    isDisabled={!isCompletionFormValid}
                  >
                    Complete Class Completion Form
                  </Button>
                </Box>
              )}
            </Flex>
          </SimpleGrid>
        </ModalBody>

        <ModalFooter gap="12px">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>

        <AlertDialog
          isOpen={isDropConfirmOpen}
          leastDestructiveRef={dropConfirmCancelRef}
          onClose={() => setIsDropConfirmOpen(false)}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="800">
                Get a sub for selected classes?
              </AlertDialogHeader>
              <AlertDialogBody>
                This will remove you from {selectedDropClassIds.length} selected class{selectedDropClassIds.length === 1 ? '' : 'es'} and open them for another instructor.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={dropConfirmCancelRef} onClick={() => setIsDropConfirmOpen(false)}>
                  Cancel
                </Button>
                <Button colorScheme="orange" onClick={handleNeedSub} ml={3} isLoading={isDropping}>
                  Yes, get a sub
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </ModalContent>
    </Modal>
  );
}
